/**
 * SmartFilterChips Component
 *
 * Visual navigation chips that provide quick access to filtered task views:
 * - Overdue: Navigate to overdue tasks view
 * - Unassigned: Navigate to unassigned tasks view
 * - Due Today: Navigate to tasks due today (future enhancement)
 *
 * Features:
 * - Toggle behavior (click to navigate)
 * - Semantic colors based on urgency
 * - Accessible with ARIA labels
 *
 * Note: Counts are placeholder values. Real counts require dedicated
 * count queries which will be added in a future backend update.
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'antd';
import AppSpinner from './AppSpinner';
import {
  ClockCircleOutlined,
  UserDeleteOutlined,
  CalendarOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { useTaskContext } from '../../../../../core/components/tasks/useTaskContext';
import '../../styles/index.scss';

const SmartFilterChips = ({
  activeFilters = [],
  onFilterChange,
  showDueToday = true,
  className = '',
}) => {
  const { currentUser } = useTaskContext();

  const [counts, setCounts] = useState({
    overdue: null,
    unassigned: null,
    dueToday: null,
  });
  const [loading, setLoading] = useState(true);

  // Initialize component - counts will be null (hidden) until proper count queries are added
  // This is a UI-only component; backend count queries will be added in a future update
  useEffect(() => {
    if (currentUser?.role?.org?.id) {
      // Brief loading state, then show chips without counts
      const timer = setTimeout(() => {
        setLoading(false);
      }, 300);

      return () => clearTimeout(timer);
    }
    // currentUser resolved (null, no org, or still undefined) — show chips without counts
    setLoading(false);
  }, [currentUser]);

  const handleChipClick = (filterType) => {
    if (onFilterChange) {
      const newFilters = activeFilters.includes(filterType)
        ? activeFilters.filter((f) => f !== filterType)
        : [...activeFilters, filterType];
      onFilterChange(newFilters);
    }
  };

  const chipConfigs = [
    {
      key: 'overdue',
      label: 'Overdue',
      count: counts.overdue,
      icon: <ClockCircleOutlined />,
      colorClass: 'task-smart-filter-chip--overdue',
      tooltip: 'View overdue tasks',
      ariaLabel: counts.overdue !== null ? `${counts.overdue} overdue tasks` : 'View overdue tasks',
    },
    {
      key: 'unassigned',
      label: 'Unassigned',
      count: counts.unassigned,
      icon: <UserDeleteOutlined />,
      colorClass: 'task-smart-filter-chip--unassigned',
      tooltip: 'View unassigned tasks',
      ariaLabel: counts.unassigned !== null ? `${counts.unassigned} unassigned tasks` : 'View unassigned tasks',
    },
    ...(showDueToday
      ? [
          {
            key: 'dueToday',
            label: 'Due Today',
            count: counts.dueToday,
            icon: <CalendarOutlined />,
            colorClass: 'task-smart-filter-chip--due-today',
            tooltip: 'View tasks due today',
            ariaLabel: counts.dueToday !== null ? `${counts.dueToday} tasks due today` : 'View tasks due today',
          },
        ]
      : []),
  ];

  if (loading) {
    return (
      <div className={`task-smart-filter-chips ${className}`} role="group" aria-label="Task filters loading">
        {chipConfigs.map((config) => (
          <div key={config.key} className={`task-smart-filter-chip task-smart-filter-chip--loading ${config.colorClass}`}>
            <AppSpinner size="small" />
            <span className="task-smart-filter-chip__label">{config.label}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className={`task-smart-filter-chips ${className}`}
      role="group"
      aria-label="Task filters"
    >
      {chipConfigs.map((config) => {
        const isActive = activeFilters.includes(config.key);

        return (
          <Tooltip key={config.key} title={config.tooltip} placement="top">
            <button
              type="button"
              className={`task-smart-filter-chip ${config.colorClass} ${isActive ? 'task-smart-filter-chip--active' : ''}`}
              onClick={() => handleChipClick(config.key)}
              aria-pressed={isActive}
              aria-label={config.ariaLabel}
            >
              <span className="task-smart-filter-chip__icon" aria-hidden="true">
                {config.icon}
              </span>
              {config.count !== null && (
                <span className="task-smart-filter-chip__count">{config.count}</span>
              )}
              <span className="task-smart-filter-chip__label">{config.label}</span>
              {isActive && (
                <span className="task-smart-filter-chip__check" aria-hidden="true">
                  <CheckOutlined />
                </span>
              )}
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
};

SmartFilterChips.propTypes = {
  activeFilters: PropTypes.arrayOf(PropTypes.oneOf(['overdue', 'unassigned', 'dueToday'])),
  onFilterChange: PropTypes.func,
  showDueToday: PropTypes.bool,
  className: PropTypes.string,
};

export default SmartFilterChips;
